import { ErrorBoundary } from 'shared-ui';

import { fetchMonitoringObecnosci, fetchPrzejscieSerwery, fetchStrefy } from 'api/strefy/strefy';
import { PRZEJSCIE_MIN_STREF } from 'api/strefy/strefy.enum';
import { Functionality } from 'api/userPermissions/userPermissions.enum';
import { QueryKeyEnum } from 'core/global.enum';
import { QueryKeyLocalStorageEnum } from 'context/query/queryContext/QueryContext.enum';
import { transformTreeToFlatArray } from 'helpers/transformTreeToFlatArray';
import { useLocale } from 'hooks/useLocale/useLocale';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { useQuery } from 'hooks/useQuery/useQuery';
import { useSavedPagination } from 'hooks/useSavedPagination/useSavedPagination';
import { AppMessages } from 'i18n/messages';

import { Passages } from './Passages';

export const PassagesContainer = () => {
  const { formatMessage } = useLocale();
  const { perms } = usePermissions();

  const { page, rowsPerPage, setPage, setRowsPerPage } = useSavedPagination({
    queryKey: QueryKeyLocalStorageEnum.PASSAGES,
  });

  const { data: serversResponse, isLoading } = useQuery([QueryKeyEnum.PRZEJSCIE_SERWERY], fetchPrzejscieSerwery);
  const { data: zonesResponse, isLoading: isLoadingZones } = useQuery([QueryKeyEnum.STREFY], fetchStrefy);
  const { data: monitoring, isLoading: isLoadingMonitoring } = useQuery(
    [QueryKeyEnum.MONITORING_OBECNOSCI],
    fetchMonitoringObecnosci,
  );

  const zones = zonesResponse?.data || [];
  const canModify = !!perms?.[Functionality.DEFINICJE]?.edit;
  const isMonitoringActive = !!monitoring?.aktywny;
  // The root counts towards the minimum, so one user zone is enough (contract §2, D11).
  const hasTooFewZones = transformTreeToFlatArray(zones).length < PRZEJSCIE_MIN_STREF;

  // Add and delete are blocked without DEFINICJE:MODIFY and while Monitoring obecności runs (contract R5).
  const getModifyDisabledReason = () => {
    if (!canModify) return formatMessage({ id: AppMessages['common.noPermissions'] });
    if (isMonitoringActive) return formatMessage({ id: AppMessages['definitions.passages.monitoringActive'] });
  };

  const getAddDisabledReason = () => {
    const modifyDisabledReason = getModifyDisabledReason();
    if (modifyDisabledReason) return modifyDisabledReason;
    if (hasTooFewZones) return formatMessage({ id: AppMessages['definitions.passages.add.tooFewZones'] });
  };

  const isModifyDisabled = !canModify || isLoadingMonitoring || isMonitoringActive;

  return (
    <ErrorBoundary>
      <Passages
        servers={serversResponse?.data || []}
        zones={zones}
        isLoading={isLoading}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        isAddDisabled={isModifyDisabled || isLoadingZones || hasTooFewZones || !serversResponse?.data.length}
        addDisabledReason={getAddDisabledReason()}
        isDeleteDisabled={isModifyDisabled}
        deleteDisabledReason={getModifyDisabledReason()}
      />
    </ErrorBoundary>
  );
};
